import { BrandLogo, Button, Badge } from '@giyus/ui';
import { useTranslation } from 'react-i18next';
import { useAuth, useMe } from '../lib/auth/authContext';
import { useActiveContext } from '../lib/context/useActiveContext';

export function SelectContextPage() {
  const { t } = useTranslation(['shell', 'common']);
  const me = useMe();
  const { signOut } = useAuth();
  const { contexts, hasNetworkAccess, switchTenant, goToNetwork } = useActiveContext();
  const roleName = (role: string) =>
    t(`shell:roles.${role}`, { defaultValue: t('shell:roles.unknown', { role }) });
  return (
    <section
      className="flex min-h-svh flex-col items-center justify-center gap-7 bg-background px-6 py-12"
      data-testid="select-context"
    >
      <BrandLogo symbol label="GIYUS" className="h-12 w-auto" />
      <header className="flex flex-col gap-1.5 text-center">
        <h1 className="text-headline font-semibold tracking-tight text-heading">
          {t('shell:context.welcome', { name: me.user.displayName.split(' ')[0] })}
        </h1>
        <p className="text-body text-muted-foreground">{t('shell:context.selectMessage')}</p>
      </header>
      <div className="flex w-full max-w-190 flex-col gap-5">
        {hasNetworkAccess && (
          <Button
            variant="outline"
            className="grid h-auto w-full grid-cols-[auto_minmax(0,1fr)] gap-4 border-border-strong p-5 text-left sm:grid-cols-[auto_minmax(0,1fr)_auto]"
            onClick={goToNetwork}
            aria-label={t('shell:context.switchTo', { name: t('shell:context.network') })}
            data-testid="select-context-network"
          >
            <span
              className="grid size-10 place-items-center rounded-control bg-primary font-bold text-primary-foreground"
              aria-hidden="true"
            >
              H
            </span>
            <span className="flex flex-col gap-0.5">
              <span className="text-title font-semibold text-heading">
                {t('shell:context.network')}
              </span>
              <span className="text-body-sm font-normal text-muted-foreground">
                {t('shell:context.networkDescription')}
              </span>
            </span>
            <Badge variant="secondary" className="col-start-2 sm:col-start-auto">
              {me.networkAccess.roles.map(roleName).join(' · ')}
            </Badge>
          </Button>
        )}
        <h2 className="giyus-overline">{t('shell:context.tenants')}</h2>
        {contexts.length === 0 ? (
          <p className="text-body text-muted-foreground">{t('shell:context.noCenters')}</p>
        ) : (
          <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {contexts.map((context) => (
              <li key={context.tenantId}>
                <Button
                  variant="outline"
                  className="h-full w-full flex-col items-start gap-2.5 border-border p-5 text-left"
                  onClick={() => switchTenant(context.code)}
                  aria-label={t('shell:context.switchTo', { name: context.name })}
                  data-testid={`select-context-${context.code}`}
                >
                  <span className="text-caption font-bold tracking-wider text-muted-foreground">
                    {context.code}
                  </span>
                  <span className="text-title font-semibold text-heading">{context.name}</span>
                  <span className="text-body-sm font-normal text-muted-foreground">
                    {t('shell:home.studyCount', {
                      count: Number(context.accessibleStudySiteCount),
                    })}{' '}
                    ·{' '}
                    {[...new Set(context.effectiveRoles.map((role) => roleName(role.role)))].join(
                      ' · ',
                    )}
                  </span>
                </Button>
              </li>
            ))}
          </ul>
        )}
        <p className="border-l-2 border-border-strong pl-2.5 text-caption text-muted-foreground">
          {t('shell:context.partnerHint')}
        </p>
      </div>
      <Button
        variant="link"
        onClick={() => {
          void signOut();
        }}
      >
        {t('shell:context.signOut')}
      </Button>
    </section>
  );
}
